import {observer} from 'mobx-react';
import React from 'react';
import {Link, PageHeading, PromiseMessage, PromiseRetry} from '../../../lib/components';
import {concat_ws, dateFormat, ReactNodeGenerator} from '../../../lib/helpers';
import {routes} from '../../../pages/Routes';
import {ClassroomStatusLabels} from '../../classroom/models/classroom-models';
import {ClassroomDetailsSceneStore} from './ClassroomDetailsSceneStore';

function* renderer(store: ClassroomDetailsSceneStore): ReactNodeGenerator {
  const state = store.state;

  yield (<PageHeading key="heading"> Classroom {store.classroomId} </PageHeading>);

  yield (<PromiseMessage key="classroom-load"
                         label="Loading classroom" state={state} align="center"/>);

  yield (<PromiseRetry key="classroom-retry"
                       state={state} retry={store.load} align="center"/>);

  if (state?.isResolved !== true)
    return;

  const classroom = state.value;
  const student = classroom.Student!;
  const teacher = classroom.Teacher!;
  const lesson = classroom.Lesson;

  yield (
    <div className="row" key="classroom-details">
      <div className="col">
        <div className="card">
          <div className="card-header">
            Classroom details
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-auto">
                <label> Student </label>
                <div className="text-box">
                  <p>{concat_ws([student.FirstName, student.LastName])}</p>
                  {
                    student.Username !== null &&
                    <p>{student.Username}</p>
                  }
                  <p>{student.Email}</p>
                </div>
                <Link className="btn btn-primary"
                      to={routes.userView(student.Id)}>View student</Link>
              </div>
              <div className="col-auto">
                <label> Teacher </label>
                <div className="text-box">
                  <p>{concat_ws([teacher.FirstName, teacher.LastName])}</p>
                  {
                    teacher.Username !== null &&
                    <p>{teacher.Username}</p>
                  }
                  <p>{teacher.Email}</p>
                </div>
                <Link className="btn btn-primary"
                      to={routes.userView(teacher.Id)}>View teacher</Link>
              </div>
              <div className="col-auto">
                <label>Lesson</label>
                <div className="text-box">
                  {concat_ws([lesson?.Unit?.Name, lesson?.Name], '/')}
                </div>
              </div>
              <div className="col-auto">
                <label>Date, Time</label>
                <div className="text-box">
                  <p>{dateFormat(classroom.ScheduledStartTime, 'wymdhm', true)}</p>
                  {classroom.ActualStartTime !== null &&
                  <p>{dateFormat(classroom.ActualStartTime, 'wymdhms', true)}</p>
                  }
                  {classroom.ActualEndTime !== null &&
                  <p>{dateFormat(classroom.ActualEndTime, 'wymdhms', true)}</p>
                  }
                </div>
              </div>
              <div className="col-auto">
                <label>Status</label>
                <div className="text-box">
                  {ClassroomStatusLabels[classroom.Status]}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const events = classroom.Events!;
  if (events !== null && events.length > 0)
    yield (
      <div className="row" key="classroom-events">
        <div className="col">
          <div className="card">
            <div className="card-header">
              Classroom events
            </div>
            <div className="card-body">
              <table className="table table-sm table-bordered table-hover">
                <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Message</th>
                </tr>
                </thead>
                <tbody>
                {
                  events.map((event) => (
                    <tr key={event.Id}>
                      <td>{dateFormat(event.Timestamp, 'wymdhms', true)}</td>
                      <td>{event.Message}</td>
                    </tr>
                  ))
                }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
}

interface Props {
  id: number
}

@observer
export class ClassroomDetailsScene extends React.Component<Props> {
  readonly store: ClassroomDetailsSceneStore;

  constructor(props: Readonly<Props>) {
    super(props);

    this.store = new ClassroomDetailsSceneStore(props.id);
  }

  componentDidMount() {
    this.store.load();
  }

  render() {
    return Array.from(renderer(this.store));
  }
}
